import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Team, Event, CreateTeamRequest } from '../utils/types';
import { apiPost, apiGet, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LocationSelector from './LocationSelector';

const CreateTeam: React.FC = () => {
  const [searchParams] = useSearchParams();
  const preSelectedEventId = searchParams.get('eventId') || '';
  
  const [formData, setFormData] = useState<CreateTeamRequest>({
    name: '',
    description: '',
    event_id: preSelectedEventId,
    looking_for_members: false,
    desired_skills: []
  });
  const [location, setLocation] = useState<string>('');
  const [locationOther, setLocationOther] = useState<string>('');
  const [events, setEvents] = useState<Event[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (token && user) {
      loadEvents();
    }
  }, [token, user]);

  const loadEvents = async () => {
    if (!token) return;
    
    setEventsLoading(true);
    try {
      const eventsData = await apiGet<Event[]>('/events', token);
      // Filter out draft events for non-organizers since participants can't create teams for draft events
      const availableEvents = isOrganizer ? eventsData : eventsData.filter(event => event.state !== 'draft');
      setEvents(availableEvents);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !formData.desired_skills!.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        desired_skills: [...(prev.desired_skills || []), skill]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      desired_skills: (prev.desired_skills || []).filter((skill: string) => skill !== skillToRemove)
    }));
  };

  const handleSkillKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('You must be logged in to create a team');
      return;
    }

    // Basic validation
    if (!formData.name.trim()) {
      setError('Team name is required');
      return;
    }

    if (!formData.event_id) {
      setError('Please select an event');
      return;
    }

    // Validate location (required for non-organizers)
    if (!isOrganizer) {
      if (!location) {
        setError('Please select your location');
        return;
      }

      if (location === 'Other' && !locationOther.trim()) {
        setError('Please specify your location when selecting "Other"');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const teamData: any = {
        ...formData
      };
      
      // Only include location fields for non-organizers (who are added as team members)
      if (!isOrganizer && location) {
        teamData.creator_location = location;
        if (location === 'Other' && locationOther) {
          teamData.creator_location_other = locationOther;
        }
      }
      
      const team = await apiPost<Team>('/teams/', teamData, token);
      console.log('Team created:', team);
      navigate('/teams');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to create team');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => navigate('/teams')} className="btn btn-secondary">
          ← Back to Teams
        </button>
      </div>

      <div className="card">
        <h1>Create New Team</h1>
        
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Team Name*
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter team name"
              required
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Describe your team's goals and approach"
              rows={3}
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Event*
            </label>
            {eventsLoading ? (
              <div style={{ padding: '10px', color: '#666' }}>Loading events...</div>
            ) : (
              <select
                name="event_id"
                value={formData.event_id}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={loading}
              >
                <option value="">Select an event</option>
                {events.map(event => (
                  <option key={event.event_id} value={event.event_id}>
                    {event.name} (Registration closes: {new Date(event.registration_deadline).toLocaleDateString()})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>
              <input
                type="checkbox"
                checked={formData.looking_for_members || false}
                onChange={(e) => setFormData(prev => ({ ...prev, looking_for_members: e.target.checked }))}
                disabled={loading}
              />
              {' '}Looking for more members
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Skills Needed
            </label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={handleSkillKeyPress}
                className="form-input"
                placeholder="Add a skill (e.g., Python, React, UI/UX)"
                disabled={loading}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addSkill}
                className="btn btn-secondary"
                disabled={loading || !skillInput.trim()}
              >
                Add
              </button>
            </div>
            
            {(formData.desired_skills || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(formData.desired_skills || []).map((skill: string, index: number) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '15px',
                      fontSize: '14px'
                    }}
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        marginLeft: '8px',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                      disabled={loading}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <small style={{ color: '#666' }}>What skills are you looking for in teammates?</small>
          </div>

          {/* Location Section - Only for non-organizers */}
          {!isOrganizer && (
            <LocationSelector
              location={location}
              locationOther={locationOther}
              onLocationChange={setLocation}
              onLocationOtherChange={setLocationOther}
              required={true}
              disabled={loading}
            />
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              className="btn"
              disabled={loading || eventsLoading}
            >
              {loading ? 'Creating...' : 'Create Team'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/teams')}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeam;
